import { getStoreInstance } from '@/store';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface ConsentManagerConfig {
  cdid: string; // Consent Manager ID
  host?: string;
  cdn?: string;
  codesrc?: string;
  ab?: string;
  isEnabled?: boolean;
}

// Define Consent Manager interface
interface CmpFunction {
  (...args: any[]): void;
  commandQueue?: any[];
}

// Extend Window interface for Consent Manager
declare global {
  interface Window {
    __cmp?: CmpFunction;
    __tcfapi?: CmpFunction;
    cmp_cdid?: string;
  }
}

export class ConsentManagerProvider implements Provider {
  type = ProviderType.CONSENT_MANAGER;
  isEnabled: boolean;
  loggingEnabled = false;
  private initialized = false;
  private config: ConsentManagerConfig;

  constructor(config: ConsentManagerConfig) {
    this.config = {
      host: 'c.delivery.consentmanager.net',
      cdn: 'cdn.consentmanager.net',
      codesrc: '0',
      ab: '1',
      ...config,
    };
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Set global consent manager configuration
      this.setGlobalConfig();

      // Load Consent Manager script
      // * 由于uk bing广告数据的问题，脚本加载放在了layout中
      // await this.loadConsentManagerScript();

      // Setup event listeners to restore body scroll
      this.setupScrollRestoreListeners();

      this.initialized = true;

      if (this.loggingEnabled) {
        logger.log('Consent Manager initialized successfully');
      }

      return true;
    } catch (error) {
      logger.error('Failed to initialize Consent Manager:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('Consent Manager tracking event:', event);
      }

      // Consent Manager typically doesn't track events directly,
      // but provides consent status that other providers use
      if (window.__cmp && event.eventName) {
        window.__cmp(event.commandType || 'getConsentData', event.properties);
      }
    } catch (error) {
      logger.error('Consent Manager tracking error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  // Get consent status for other providers
  getConsentStatus(callback?: (consentData: any) => void): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (window.__cmp) {
        window.__cmp('getConsentData', null, callback);
      } else if (window.__tcfapi) {
        window.__tcfapi('getTCData', 2, callback);
      }
    } catch (error) {
      logger.error('Failed to get consent status:', error);
    }
  }

  private setGlobalConfig(): void {
    // Set global configuration that the script expects
    if (typeof window !== 'undefined') {
      window.cmp_cdid = this.config.cdid;
    }
  }

  /**
   * 启用Consent Manager日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Consent Manager日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  private loadConsentManagerScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://cdn.consentmanager.net/delivery/js/semiautomatic.min.js';

        // Set data attributes as in the original script
        script.setAttribute('data-cmp-ab', this.config.ab!);
        script.setAttribute('data-cmp-cdid', this.config.cdid);
        script.setAttribute('data-cmp-host', this.config.host!);
        script.setAttribute('data-cmp-cdn', this.config.cdn!);
        script.setAttribute('data-cmp-codesrc', this.config.codesrc!);

        script.onload = () => {
          if (this.loggingEnabled) {
            logger.log('Consent Manager script loaded successfully');
          }
          resolve();
        };

        script.onerror = () => {
          const error = new Error('Failed to load Consent Manager script');
          logger.error(error.message);
          reject(error);
        };

        // Insert script into document
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          document.head.appendChild(script);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 设置事件监听器，在用户同意或拒绝后恢复 body 滚动
   */
  private setupScrollRestoreListeners(): void {
    if (typeof window === 'undefined') return;

    const maxRetries = 50; // 最多重试50次（5秒）
    let retryCount = 0;

    // 恢复 body 滚动的函数
    const restoreBodyScroll = () => {
      try {
        // 检查快捷登录注册弹窗是否打开，打开的话还是需要禁止body滚动
        const storeInstance = getStoreInstance();
        if (storeInstance?.getState()?.quickCouponDialog?.show) {
          if (this.loggingEnabled) {
            logger.log(
              'Consent Manager: Skip restoring body scroll, quick login register dialog is open',
            );
          }
          return;
        }

        if (document.body) {
          document.body.style.overflow = 'auto';
        }
        if (this.loggingEnabled) {
          logger.log('Consent Manager: Body scroll restored');
        }
      } catch (error) {
        logger.error('Consent Manager: Failed to restore body scroll', error);
      }
    };

    // 持续监控并恢复样式的函数
    const restoreBodyScrollWithMonitoring = () => {
      let checkCount = 0;
      const maxChecks = 10; // 最多检查10次（总共约2秒）
      const checkInterval = 200; // 每200ms检查一次

      const checkAndRestore = () => {
        // 检查 body 的 overflow 是否为 hidden
        const bodyOverflow =
          document.body?.style.overflow || window.getComputedStyle(document.body).overflow;

        // 如果样式被设置为 hidden，则恢复
        if (bodyOverflow === 'hidden') {
          restoreBodyScroll();
        }

        checkCount++;
        if (checkCount < maxChecks) {
          setTimeout(checkAndRestore, checkInterval);
        }
      };

      // 延迟 300ms 后开始监控，确保 consent manager 完成初始操作
      setTimeout(() => {
        restoreBodyScroll(); // 立即恢复一次
        checkAndRestore(); // 开始持续监控
      }, 300);
    };

    // 监听 consentapproved 事件
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleConsentApproved = (_eventname: string, _cmpobject: any, _subtype: any) => {
      restoreBodyScrollWithMonitoring();
    };

    // 监听 consentrejected 事件
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleConsentRejected = (_eventname: string, _cmpobject: any, _subtype: any) => {
      restoreBodyScrollWithMonitoring();
    };

    // 监听 consent 事件（通用事件，用户做出任何选择后都会触发）
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleConsent = (_eventname: string, _cmpobject: any, _subtype: any) => {
      restoreBodyScrollWithMonitoring();
    };

    // 等待 __cmp API 可用后注册事件监听器
    const setupListeners = () => {
      try {
        if (
          typeof window !== 'undefined' &&
          typeof window.__cmp !== 'undefined' &&
          typeof window.__cmp === 'function'
        ) {
          // 监听 consentapproved 事件
          window.__cmp('addEventListener', ['consentapproved', handleConsentApproved, false], null);
          // 监听 consentrejected 事件
          window.__cmp('addEventListener', ['consentrejected', handleConsentRejected, false], null);
          // 监听 consent 事件（作为备用）
          window.__cmp('addEventListener', ['consent', handleConsent, false], null);

          if (this.loggingEnabled) {
            logger.log('Consent Manager: Event listeners setup successfully');
          }
        } else if (retryCount < maxRetries) {
          // 如果 __cmp 还未加载，等待一段时间后重试
          retryCount++;
          setTimeout(setupListeners, 100);
        } else {
          logger.warn('Consent Manager: Failed to setup event listeners after max retries');
        }
      } catch (error) {
        logger.error('Consent Manager: Failed to setup event listeners', error);
      }
    };

    // 延迟设置监听器，确保脚本已完全加载
    setTimeout(setupListeners, 100);
  }
}
